import type { CatalogStem } from '@/domain/catalog/stem';
import type { StemSwitchAnchor } from '@/views/3d-planner/fitting/stemTransform';

/**
 * Determines how to carry over a manual stem adjustment when switching stems,
 * based on what changed between the old and new catalog entries.
 *
 * Returns the anchor to use for resection-alignment, or `null` to reset to the auto-fit.
 *
 * Decision table:
 * - Different `system` or `profile` (stem-type change) → `null` (reset)
 * - Same offset, different size                       → `'head-centre'`
 * - Different offset, same size                       → `'resection-origin'`
 * - Otherwise (both differ, or no diff)               → `null`
 */
export function stemChangeAnchor(
    oldStem: CatalogStem,
    newStem: CatalogStem,
): StemSwitchAnchor | null {
    if (oldStem.system !== newStem.system || oldStem.profile !== newStem.profile) {
        return null;
    }
    const sameOffset = oldStem.offset === newStem.offset;
    const sameSize = oldStem.size === newStem.size;
    if (sameOffset && !sameSize) return 'head-centre';
    if (!sameOffset && sameSize) return 'resection-origin';
    return null;
}
